import React, { ReactElement, useEffect, useState } from 'react';

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import { DragIndicatorIcon, Row, Col, Container, Button } from 'elmo-elements';

import { ScrollbarIndicator } from '@/lib/util';

import ChoiceOption from '@/components/ChoiceOption';

import './DragDrop.scss';
import { ChoiceMetadataType, ChoiceOptionType } from '@/modules/types/choice';

interface ChoiceDraggableOptionType extends ChoiceOptionType {
  droppedItemId?: string;
  isVisible?: boolean;
}

interface DndItemsType {
  [index: string]: ChoiceDraggableOptionType;
}

interface AnswersType {
  [index: string]: string;
}

function useRerenderHook(): () => void {
  const [rerender, setRerender] = useState(0);
  return () => setRerender(rerender + 1);
}

export const DragDropButtons = ({
  isButtonsVisible,
  onReset,
  onSubmit,
}: {
  isButtonsVisible: boolean;
  onReset: () => void;
  onSubmit: () => void;
}): ReactElement =>
  !isButtonsVisible ? (
    <div />
  ) : (
    <div className="elmoca-dragdrop__buttons mt-2 float-right">
      <Button type="default" onClick={onReset} className="mr-2">
        Reset
      </Button>
      <Button type="primary" onClick={onSubmit}>
        Submit
      </Button>
    </div>
  );

export const RenderDragItems = ({
  dragItems,
}: {
  dragItems: DndItemsType;
}): ReactElement => (
  <>
    {Object.keys(dragItems).map((key, index) => {
      const item = dragItems[key];

      return (
        <Droppable
          droppableId={`${item.data.id}`}
          key={item.data.id}
          isDropDisabled={true}
        >
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="elmoca-dnd-draglist-wrapper"
            >
              {!item.isVisible ? (
                <div />
              ) : (
                <Draggable draggableId={`${item.data.id}`} index={index}>
                  {(providedDrag) => (
                    <div
                      ref={providedDrag.innerRef}
                      {...providedDrag.draggableProps}
                      {...providedDrag.dragHandleProps}
                      className="elmoca-dnd-draglist-item"
                    >
                      <ChoiceOption
                        title=""
                        description={item.data.content}
                        icon={<DragIndicatorIcon />}
                        className="mb-0"
                        iconRight=""
                      />
                    </div>
                  )}
                </Draggable>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      );
    })}
  </>
);

export const RenderDropItems = ({
  answers,
  dropItems,
}: {
  answers: DndItemsType;
  dropItems: DndItemsType;
}): ReactElement => (
  <>
    {Object.keys(answers).map((key, index) => {
      const item = answers[key];
      const droppedItem =
        item.droppedItemId !== undefined ? dropItems[item.droppedItemId] : '';
      return (
        <Row
          className="elmoca-dnd-droplist-item"
          alignItems="center"
          isNoGutters={true}
          key={item.data.id}
        >
          <Col span={12}>
            <div className="elmoca-dnd-droplist-item-text">
              {item.data.content}
            </div>
          </Col>
          <Col span={12}>
            <Droppable
              droppableId={`${item.data.id}`}
              isDropDisabled={!!droppedItem}
            >
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={`elmoca-dnd-droplist-item-target ${
                    snapshot.isDraggingOver
                      ? 'elmoca-dnd-droplist-item-target__over'
                      : ''
                  }`}
                >
                  {!droppedItem ? (
                    <div />
                  ) : (
                    <Draggable
                      draggableId={`${droppedItem.data.id}`}
                      index={index}
                    >
                      {(providedDrag) => (
                        <div
                          ref={providedDrag.innerRef}
                          {...providedDrag.draggableProps}
                          {...providedDrag.dragHandleProps}
                          className="elmoca-dnd-draglist-item"
                        >
                          <ChoiceOption
                            title=""
                            description={droppedItem.data.content}
                            icon={<DragIndicatorIcon />}
                            className="mb-0"
                            iconRight=""
                          />
                        </div>
                      )}
                    </Draggable>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </Col>
        </Row>
      );
    })}
  </>
);

const createDndItems = (
  itemChoices: ChoiceOptionType[],
  type: number,
): DndItemsType => {
  const dndItems = {} as DndItemsType;
  [...itemChoices]
    .filter(
      (item) =>
        item.data.choiceMetadata && +item.data.choiceMetadata.type === type,
    )
    .forEach((item) => {
      dndItems[item.data.id] = {
        ...item,
        isVisible:
          item.data.choiceMetadata &&
          +item.data.choiceMetadata.type === ChoiceMetadataType.secondary,
        droppedItemId: '',
      };
    });

  return dndItems;
};

const DragDrop = ({
  choices,
  handler,
}: {
  choices: ChoiceOptionType[];
  handler: (answer: string) => void;
}): ReactElement => {
  const [isButtonsVisible, setIsButtonsVisible] = useState(false);
  const [answers, setAnswers] = useState(
    createDndItems(choices, ChoiceMetadataType.main),
  );
  const [dragItems, setDragItems] = useState(
    createDndItems(choices, ChoiceMetadataType.secondary),
  );
  const [dropItems, setDropItems] = useState(
    createDndItems(choices, ChoiceMetadataType.secondary),
  );
  const doRerender = useRerenderHook();

  const removeDragItem = (dragId: string) => {
    const dragItemsReturn = dragItems;
    dragItemsReturn[dragId] = {
      ...dragItems[dragId],
      isVisible: false,
    };
    setDragItems(dragItemsReturn);
  };

  useEffect(() => {
    setAnswers(createDndItems(choices, ChoiceMetadataType.main));
    setDragItems(createDndItems(choices, ChoiceMetadataType.secondary));
    setDropItems(createDndItems(choices, ChoiceMetadataType.secondary));
  }, [choices]);

  const showButtons = () => {
    setIsButtonsVisible(true);
  };

  const hideButtons = () => {
    setIsButtonsVisible(false);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return false;
    const answersReturn = answers;
    if (answersReturn[result.source.droppableId]) {
      // remove a drag from answers
      answersReturn[result.source.droppableId] = {
        ...answers[result.source.droppableId],
        droppedItemId: undefined,
      };
    }
    answersReturn[result.destination.droppableId] = {
      // remove a drag from options
      ...answers[result.destination.droppableId],
      droppedItemId: result.draggableId,
    };

    setAnswers(answersReturn);
    removeDragItem(result.draggableId);
    showButtons();
    doRerender();
    return false;
  };

  const onReset = () => {
    setAnswers(createDndItems(choices, ChoiceMetadataType.main));
    setDragItems(createDndItems(choices, ChoiceMetadataType.secondary));
    setDropItems(createDndItems(choices, ChoiceMetadataType.secondary));
    hideButtons();
  };

  const onSubmit = () => {
    hideButtons();

    const returnAnswer = {} as AnswersType;
    Object.keys(answers).map((key, index) => {
      const { droppedItemId } = answers[key];
      if (droppedItemId !== undefined) returnAnswer[key] = droppedItemId;
      return false;
    });

    handler(JSON.stringify(returnAnswer));
  };

  return (
    <div data-testid="elmoca-dragdrop">
      <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
        <div className="elmoca-dragdrop__wrapper" ref={ScrollbarIndicator()}>
          <Container>
            <Row className="elmoca-dnd">
              <Col className="elmoca-dnd-droplist" span={16}>
                <RenderDropItems answers={answers} dropItems={dropItems} />
              </Col>
              <Col className="elmoca-dnd-draglist" span={8}>
                <RenderDragItems dragItems={dragItems} />
              </Col>
            </Row>
          </Container>
        </div>
      </DragDropContext>
      <DragDropButtons
        isButtonsVisible={isButtonsVisible}
        onReset={onReset}
        onSubmit={onSubmit}
      />
    </div>
  );
};

export default DragDrop;
